export default function componentStyleOverrides(theme) {
  const t = theme.t;
  const focusRing = `0 0 0 3px ${t.primarySoft}`;

  const softTone = {
    success: { bg: t.successSoft, border: t.successBorder, fg: t.successText, icon: t.success },
    info: { bg: t.infoSoft, border: t.infoBorder, fg: t.infoText, icon: t.info },
    warning: { bg: t.warnSoft, border: t.warnBorder, fg: t.warnText, icon: t.warn },
    error: { bg: t.dangerSoft, border: t.dangerBorder, fg: t.dangerText, icon: t.danger }
  };

  const popupPaper = {
    backgroundColor: t.panel,
    backgroundImage: 'none',
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    boxShadow: t.shadows.popup
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          colorScheme: t.mode,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        },
        body: {
          backgroundColor: t.bg,
          color: t.text
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${t.border} transparent`
        },
        '::selection': {
          backgroundColor: t.primarySoft,
          color: t.text
        },
        '.mono': {
          fontFamily: t.fonts.mono,
          fontVariantNumeric: 'tabular-nums'
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            transitionDuration: '0.01ms !important',
            animationDuration: '0.01ms !important'
          }
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: { mono: 'span' }
      }
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          boxShadow: 'none',
          transition: 'background-color .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease',
          '&.Mui-focusVisible': {
            boxShadow: focusRing
          }
        },
        sizeSmall: { minHeight: 32, padding: '4px 12px', fontSize: '0.8125rem' },
        sizeMedium: { minHeight: 40, padding: '8px 16px' },
        sizeLarge: { minHeight: 46, padding: '10px 20px', fontSize: '0.9375rem' },

        text: ({ ownerState }) =>
          ownerState.color === 'error'
            ? {
                color: t.danger,
                '&:hover': { backgroundColor: t.dangerSoft }
              }
            : {
                backgroundColor: t.primary,
                color: t.onPrimary,
                '&:hover': { backgroundColor: t.primaryHover },
                '&.Mui-disabled': { backgroundColor: t.hover, color: t.text3 }
              },

        contained: ({ ownerState }) => ({
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          ...(ownerState.color === 'primary' || ownerState.color === 'secondary' || ownerState.color === 'inherit'
            ? {
                backgroundColor: t.primary,
                color: t.onPrimary,
                '&:hover': { backgroundColor: t.primaryHover, boxShadow: 'none' }
              }
            : {}),
          '&.Mui-disabled': { backgroundColor: t.hover, color: t.text3 }
        }),

        outlined: ({ ownerState }) => ({
          backgroundColor: t.panel,
          borderColor: t.border,
          color: ownerState.color === 'error' ? t.danger : t.text,
          '&:hover': {
            backgroundColor: ownerState.color === 'error' ? t.dangerSoft : t.hover,
            borderColor: ownerState.color === 'error' ? t.dangerBorder : t.text3
          },
          '&.Mui-disabled': { borderColor: t.border, color: t.text3 }
        })
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          '&:hover': { backgroundColor: t.hover },
          '&.Mui-focusVisible': { boxShadow: focusRing }
        }
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: t.hover,
          padding: 3,
          borderRadius: 10,
          gap: 2
        },
        grouped: {
          border: 0,
          borderRadius: 8,
          margin: 0,
          '&:not(:first-of-type)': { borderRadius: 8, marginLeft: 0, borderLeft: 0 },
          '&:not(:last-of-type)': { borderRadius: 8 }
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          lineHeight: 1.4,
          padding: '6px 14px',
          border: 0,
          color: t.text2,
          '&:hover': { backgroundColor: 'transparent', color: t.text },
          '&.Mui-selected': {
            backgroundColor: t.panel,
            color: t.text,
            boxShadow: t.shadows.active,
            '&:hover': { backgroundColor: t.panel }
          },
          '&.Mui-focusVisible': { boxShadow: focusRing }
        }
      }
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        rounded: {
          borderRadius: 14
        },
        outlined: {
          borderColor: t.border
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: t.panel,
          boxShadow: t.mode === 'dark' ? `0 0 0 1px ${t.border}` : `0 0 0 1px ${t.border}, ${t.shadows.sm}`
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: t.text,
          padding: '20px 24px'
        },
        title: {
          fontSize: '1rem',
          fontWeight: 700
        },
        subheader: {
          color: t.text3,
          fontSize: '0.8125rem'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundColor: t.panel,
          border: `1px solid ${t.border}`,
          borderRadius: 12,
          '&:before': { display: 'none' },
          '&:not(:last-of-type)': { marginBottom: 8 }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: t.border,
          opacity: 1
        }
      }
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: t.text2,
          borderRadius: 10,
          paddingTop: '10px',
          paddingBottom: '10px',
          '& .MuiListItemIcon-root': { color: t.text3 },
          '&:hover': {
            backgroundColor: t.hover,
            color: t.text,
            '& .MuiListItemIcon-root': { color: t.text }
          },
          '&.Mui-selected': {
            color: t.text,
            backgroundColor: t.sel,
            '& .MuiListItemIcon-root': { color: t.primary },
            '&:hover': { backgroundColor: t.sel }
          },
          '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${t.primary200}` }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: t.text3,
          minWidth: '36px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'inherit'
        },
        secondary: {
          color: t.text3
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: popupPaper
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: popupPaper,
        list: {
          padding: 6
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 36,
          fontSize: '0.875rem',
          '&:hover': { backgroundColor: t.hover },
          '&.Mui-focusVisible': { backgroundColor: t.hover },
          '&.Mui-selected': {
            backgroundColor: t.sel,
            '&:hover': { backgroundColor: t.sel },
            '&.Mui-focusVisible': { backgroundColor: t.sel }
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: popupPaper,
        listbox: {
          padding: 6,
          '& .MuiAutocomplete-option': {
            borderRadius: 8,
            minHeight: 36,
            '&.Mui-focused': { backgroundColor: t.hover },
            '&[aria-selected="true"]': { backgroundColor: t.sel },
            '&[aria-selected="true"].Mui-focused': { backgroundColor: t.sel }
          }
        },
        tag: {
          borderRadius: 999
        }
      }
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          color: t.text,
          '&::placeholder': {
            color: t.text3,
            opacity: 1,
            fontSize: '0.875rem'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: t.panel,
          borderRadius: 10,
          transition: 'box-shadow .15s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: t.border,
            transition: 'border-color .15s ease'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.text3
          },
          '&.Mui-focused': {
            boxShadow: focusRing
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.primary,
            borderWidth: 1
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: t.danger
          },
          '&.Mui-error.Mui-focused': {
            boxShadow: `0 0 0 3px ${t.dangerSoft}`
          },
          '&.MuiInputBase-multiline': {
            padding: 1
          },
          '&.Mui-disabled': {
            backgroundColor: t.canvas
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: t.border
          },
          '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.border
          }
        },
        input: {
          fontWeight: 500,
          padding: '15.5px 14px',
          borderRadius: 10,
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${t.panel} inset`,
            WebkitTextFillColor: t.text,
            caretColor: t.text
          },
          '&.MuiInputBase-inputSizeSmall': {
            padding: '10px 14px',
            '&.MuiInputBase-inputAdornedStart': {
              paddingLeft: 0
            }
          }
        },
        inputAdornedStart: {
          paddingLeft: 4
        },
        notchedOutline: {
          borderRadius: 10
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: t.text3,
          '&.Mui-focused': { color: t.primaryText },
          '&.Mui-error': { color: t.danger }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: t.text3,
          '&.Mui-error': { color: t.danger }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: t.text2,
          '&.Mui-focused': { color: t.primaryText }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: t.text3
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: t.text3
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: t.text3
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: t.border
          }
        },
        mark: {
          backgroundColor: t.panel,
          width: '4px'
        },
        valueLabel: {
          color: t.onPrimary,
          backgroundColor: t.primary
        }
      }
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          color: t.primaryText,
          backgroundColor: t.primarySoft
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
          fontSize: '0.75rem',
          '&.MuiChip-deletable .MuiChip-deleteIcon': {
            color: 'inherit'
          }
        },
        filled: ({ ownerState }) =>
          ownerState.color === 'default' || !ownerState.color
            ? { backgroundColor: t.hover, color: t.text2 }
            : {},
        outlined: {
          borderColor: t.border
        }
      }
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600
        }
      }
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true
      },
      styleOverrides: {
        tooltip: {
          color: t.bg,
          backgroundColor: t.text,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 10px'
        },
        arrow: {
          color: t.text
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${t.border}`
        },
        head: {
          backgroundColor: 'transparent',
          color: t.text3,
          fontWeight: 600,
          fontSize: '0.8125rem'
        },
        body: {
          color: t.text,
          fontSize: '0.875rem'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: t.hover },
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: t.sel }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: t.text2 },
        selectLabel: { color: t.text3 },
        displayedRows: { color: t.text2 }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: t.primary,
          height: 2,
          borderRadius: 2
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          color: t.text2,
          '&.Mui-selected': { color: t.text }
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { color: t.text3 },
        li: { color: t.text2 }
      }
    },

    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const tone = softTone[ownerState.severity] || softTone.info;
          if (ownerState.variant === 'filled') return { borderRadius: 12 };
          return {
            borderRadius: 12,
            backgroundColor: tone.bg,
            color: tone.fg,
            border: `1px solid ${tone.border}`,
            '& .MuiAlert-icon': { color: tone.icon }
          };
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: t.panel,
          border: `1px solid ${t.border}`,
          borderRadius: 16,
          boxShadow: t.shadows.popup
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 700,
          color: t.text
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 8
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.invisible ? {} : { backgroundColor: t.mode === 'dark' ? 'rgba(0,0,0,.6)' : 'rgba(15,15,30,.35)' }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, backgroundColor: t.hover }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: t.hover }
      }
    }
  };
}